/**
 * Componente KPISection
 * 
 * Seção de KPIs do dashboard com renderização baseada em configuração,
 * skeleton loading, animações suaves e controle de visibilidade.
 * 
 * @module components/Dashboard/KPISection/KPISection
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import KPICard from './KPICard';
import KPICardSkeleton from './KPICardSkeleton';
import kpiConfig, { getKPIsByCategory, generateKPIProps } from '../../../utils/dashboard/kpiConfig';
import Alert from '../../UI/Alert';
import LoadingIndicator from '../../Common/LoadingIndicator';

/**
 * Componente de Seção de KPIs
 * 
 * Renderiza uma seção de KPIs com:
 * - Configuração baseada em kpiConfig.js
 * - Skeleton loading durante carregamento
 * - Animações suaves de entrada/saída
 * - Controle de visibilidade (mostrar/ocultar)
 * - Separação entre KPIs principais e alertas
 * 
 * @param {object} props - Propriedades do componente
 * @param {Object} props.summary - Dados do summary do dashboard
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {string|null} props.error - Mensagem de erro (null se não houver)
 * @param {boolean} [props.showByDefault=true] - Se deve mostrar KPIs por padrão
 * @param {boolean} [props.showToggle=true] - Se deve mostrar botão de toggle
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de seção de KPIs
 * 
 * @example
 * ```jsx
 * <KPISection
 *     summary={summary}
 *     isLoading={isLoading}
 *     error={error}
 * />
 * ```
 */
const KPISection = ({
    summary = null,
    isLoading,
    error = null,
    showByDefault = true,
    showToggle = true,
    className = '',
    visibleKPIs = null, // Array de chaves de KPIs visíveis (null = mostrar todos)
}) => {
    const [showKPIs, setShowKPIs] = React.useState(showByDefault);

    // Gerar props dos KPIs principais
    // Memoizado para evitar recálculo desnecessário
    const mainKPIs = useMemo(() => {
        if (!summary) return [];
        const mainKpiConfigs = getKPIsByCategory('main');
        const kpis = mainKpiConfigs.map(config => ({
            kpiKey: config.key,
            ...generateKPIProps(config, summary),
        }));
        // Filtrar por visibleKPIs se fornecido
        return visibleKPIs 
            ? kpis.filter(kpi => visibleKPIs.includes(kpi.kpiKey))
            : kpis;
    }, [summary, visibleKPIs]);

    // Gerar props dos KPIs de alerta
    // Memoizado para evitar recálculo desnecessário
    const alertKPIs = useMemo(() => {
        if (!summary) return [];
        const alertKpiConfigs = getKPIsByCategory('alert');
        const kpis = alertKpiConfigs
            .map(config => ({
                kpiKey: config.key,
                ...generateKPIProps(config, summary),
            }))
            .filter(kpi => kpi.value !== undefined && kpi.value !== null);
        // Filtrar por visibleKPIs se fornecido
        return visibleKPIs 
            ? kpis.filter(kpi => visibleKPIs.includes(kpi.kpiKey))
            : kpis;
    }, [summary, visibleKPIs]);

    // Memoizar função de toggle para evitar re-renderizações
    const handleToggle = React.useCallback(() => {
        setShowKPIs(prev => !prev);
    }, []);

    // Animação de entrada para cada card
    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: (index) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: index * 0.05, // Delay escalonado para efeito cascata
                duration: 0.4,
                ease: 'easeOut',
            },
        }),
    };

    // Renderizar skeleton loading
    if (isLoading) {
        return (
            <div className={className} data-testid="kpi-section-loading">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-6">
                    {Array.from({ length: 8 }).map((_, index) => (
                        <KPICardSkeleton key={`skeleton-${index}`} />
                    ))}
                </div>
            </div>
        );
    }

    // Renderizar erro
    if (error) {
        return (
            <div className={className} data-testid="kpi-section-error">
                <Alert type="error" message={error} />
            </div>
        );
    }

    // Renderizar aviso se não houver dados
    if (!summary) {
        return (
            <div className={className} data-testid="kpi-section-no-data">
                <Alert 
                    type="warning" 
                    message="Não foi possível carregar os dados do dashboard. Por favor, recarregue a página." 
                />
            </div>
        );
    }

    return (
        <section 
            id="kpi-section"
            className={className} 
            data-testid="kpi-section"
            aria-labelledby="kpi-section-title"
            aria-label="Seção de Métricas de Performance"
        >
            {/* Título oculto para leitores de tela */}
            <h2 id="kpi-section-title" className="sr-only">
                Métricas de Performance
            </h2>
            
            {/* Botão de toggle (mostrar/ocultar) */}
            {showToggle && (
                <div className="mb-4 flex justify-end">
                    <button
                        onClick={handleToggle}
                        onKeyDown={(e) => {
                            // Suporte para Enter e Space
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleToggle();
                            }
                        }}
                        className="
                            group
                            flex items-center gap-2 
                            px-5 py-2.5 
                            text-sm font-semibold
                            text-gray-700 
                            bg-gradient-to-r from-white to-gray-50
                            border border-gray-200 
                            rounded-xl 
                            shadow-sm
                            hover:from-gray-50 hover:to-gray-100
                            hover:border-emerald-300
                            hover:shadow-md
                            hover:scale-105
                            active:scale-100
                            active:from-gray-100 active:to-gray-200
                            transition-all duration-300 ease-in-out
                            focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                        "
                        aria-label={showKPIs ? 'Ocultar métricas de performance' : 'Mostrar métricas de performance'}
                        aria-expanded={showKPIs}
                        aria-controls="kpi-cards-container"
                        type="button"
                    >
                        {showKPIs ? (
                            <>
                                <FaChevronUp 
                                    className="text-gray-500 group-hover:text-emerald-600 transition-colors duration-200" 
                                    size={14} 
                                    aria-hidden="true"
                                />
                                <span className="group-hover:text-emerald-700 transition-colors duration-200">
                                    Ocultar Métricas
                                </span>
                            </>
                        ) : (
                            <>
                                <FaChevronDown 
                                    className="text-gray-500 group-hover:text-emerald-600 transition-colors duration-200" 
                                    size={14} 
                                    aria-hidden="true"
                                />
                                <span className="group-hover:text-emerald-700 transition-colors duration-200">
                                    Mostrar Métricas
                                </span>
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Container de KPIs com animação */}
            <AnimatePresence>
                {showKPIs && (
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={{
                            hidden: {
                                opacity: 0,
                                height: 0,
                                marginBottom: 0,
                            },
                            visible: {
                                opacity: 1,
                                height: 'auto',
                                marginBottom: 24,
                                transition: {
                                    duration: 0.5,
                                    ease: 'easeInOut',
                                },
                            },
                        }}
                        style={{
                            overflow: 'hidden',
                        }}
                        id="kpi-cards-container"
                        aria-live="polite"
                        aria-atomic="false"
                    >
                        {/* KPIs Principais */}
                        <div 
                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-6 md:gap-8 mb-4 sm:mb-6 pt-2 pb-2"
                            role="list"
                            aria-label="Métricas principais"
                        >
                            <AnimatePresence>
                                {mainKPIs.map((kpi, index) => {
                                    const { kpiKey, ...kpiProps } = kpi;
                                    return (
                                        <motion.div
                                            key={kpiKey}
                                            custom={index}
                                            initial="hidden"
                                            animate="visible"
                                            exit="hidden"
                                            variants={cardVariants}
                                            role="listitem"
                                        >
                                            <KPICard {...kpiProps} testId={`kpi-${kpiKey}`} />
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </div>

                        {/* KPIs de Alertas (separados) */}
                        {alertKPIs.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: mainKPIs.length * 0.05 + 0.2, duration: 0.4 }}
                                className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-6 md:gap-8 mb-4 sm:mb-6 pt-2 pb-2"
                                role="list"
                                aria-label="Métricas de alerta"
                            >
                                {alertKPIs.map((kpi, index) => {
                                    const { kpiKey, ...kpiProps } = kpi;
                                    return (
                                        <motion.div
                                            key={kpiKey}
                                            custom={index}
                                            initial="hidden"
                                            animate="visible"
                                            variants={cardVariants}
                                            role="listitem"
                                        >
                                            <KPICard {...kpiProps} testId={`kpi-alert-${kpiKey}`} />
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

KPISection.propTypes = {
    /** Dados do summary do dashboard */
    summary: PropTypes.object,
    
    /** Indica se está carregando */
    isLoading: PropTypes.bool.isRequired,
    
    /** Mensagem de erro (null se não houver) */
    error: PropTypes.string,
    
    /** Se deve mostrar KPIs por padrão */
    showByDefault: PropTypes.bool,
    
    /** Se deve mostrar botão de toggle */
    showToggle: PropTypes.bool,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
    
    /** Array de chaves de KPIs visíveis (null = mostrar todos) */
    visibleKPIs: PropTypes.arrayOf(PropTypes.string),
};

// Default props removidos - usando default parameters na função

// Memoizar componente para evitar re-renderizações desnecessárias
export default React.memo(KPISection, (prevProps, nextProps) => {
    // Comparação customizada para evitar re-renderizações desnecessárias
    // Compara arrays de visibleKPIs verificando se têm o mesmo tamanho e mesmos elementos
    const visibleKPIsEqual = prevProps.visibleKPIs === nextProps.visibleKPIs ||
        (Array.isArray(prevProps.visibleKPIs) && Array.isArray(nextProps.visibleKPIs) &&
         prevProps.visibleKPIs.length === nextProps.visibleKPIs.length &&
         prevProps.visibleKPIs.every((kpi, index) => kpi === nextProps.visibleKPIs[index]));
    
    return (
        prevProps.summary === nextProps.summary &&
        prevProps.isLoading === nextProps.isLoading &&
        prevProps.error === nextProps.error &&
        prevProps.showByDefault === nextProps.showByDefault &&
        prevProps.showToggle === nextProps.showToggle &&
        prevProps.className === nextProps.className &&
        visibleKPIsEqual
    );
});

